/**
 * A wiki page ref, as it reaches the graph panel, reduced to the page it names.
 *
 * The backend sends each page's own path in the wiki ("themes/Shabbat.md",
 * wiki_tools.canonical_page). This is the same reduction on the client, so a
 * ref in any other shape still names ONE node with a clean label: the packaging
 * a model wraps a name in (quotes, list brackets, an Obsidian `[[...|alias]]`)
 * comes off, and so do a `#heading`, the folder and the `.md`. Seen on staging
 * (item 57): '["Renewable Energy.md"' and '"Shabbat.md"]' drew a second,
 * raw-labelled node beside each real page and the same text in the chips.
 *
 * Mirrors backend/modules/skills/tools/adk/wiki/wiki_tools.clean_page_ref.
 */

// Quote marks a model wraps a name in, straight and typographic.
const QUOTES = new Set(['"', "'", '`', '“', '”', '‘', '’']);

function stripQuotes(s: string): string {
  let start = 0;
  let end = s.length;
  while (start < end && QUOTES.has(s[start])) start += 1;
  while (end > start && QUOTES.has(s[end - 1])) end -= 1;
  return s.slice(start, end);
}

/** The ref without its packaging; the name inside is left as written. */
export function cleanPageRef(ref: unknown): string {
  let s = String(ref ?? '').trim();
  for (;;) {
    const before = s;
    if (s.startsWith('[[') && s.endsWith(']]')) s = s.slice(2, -2).split('|')[0];
    s = stripQuotes(s.trim()).trim();
    // A stray bracket left by a split list: '["A.md"' / '"B.md"]'.
    s = s.replace(/^\[+/, '').replace(/\]+$/, '').trim();
    if (s === before) return s;
  }
}

/** The page's path: cleaned, forward slashes, no `#heading`. */
export function wikiPagePath(ref: unknown): string {
  return cleanPageRef(ref).replace(/\\/g, '/').replace(/#.*$/, '').trim();
}

/** What a label shows: the page's name — no folder, no `.md`. */
export function wikiPageName(ref: unknown): string {
  const path = wikiPagePath(ref);
  const last = path.split('/').filter(Boolean).pop() || path;
  return last.replace(/\.md$/i, '').trim();
}

/** One node per page: its name, case-folded — the wiki resolves a bare name the same way. */
export function wikiPageKey(ref: unknown): string {
  return wikiPageName(ref).toLowerCase();
}

/** The top folder the page sits in, '' at the wiki's root. */
export function wikiPageFolder(ref: unknown): string {
  const parts = wikiPagePath(ref).split('/').filter(Boolean);
  return parts.length > 1 ? parts[0] : '';
}
